import { useState, useRef, useEffect, useCallback } from 'react'
import PrivacyPolicy from '../components/PrivacyPolicy'

/* ── Circle Crop Modal ── */
function CropModal({ imageUrl, onConfirm, onCancel }) {
  const canvasRef = useRef(null)
  const imgRef = useRef(null)
  const containerRef = useRef(null)

  const [imgLoaded, setImgLoaded] = useState(false)
  const [scale, setScale] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const posStart = useRef({ x: 0, y: 0 })
  const lastPinchDist = useRef(null)
  const circleSize = 280

  useEffect(() => {
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imgRef.current = img
      const minDim = Math.min(img.width, img.height)
      const initScale = circleSize / minDim
      setScale(initScale)
      setPos({
        x: -(img.width * initScale - circleSize) / 2,
        y: -(img.height * initScale - circleSize) / 2,
      })
      setImgLoaded(true)
    }
    img.src = imageUrl
  }, [imageUrl])

  const handlePointerDown = (e) => {
    e.preventDefault()
    setDragging(true)
    const pt = e.touches ? e.touches[0] : e
    dragStart.current = { x: pt.clientX, y: pt.clientY }
    posStart.current = { ...pos }
    if (e.touches && e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      lastPinchDist.current = Math.hypot(dx, dy)
    }
  }

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return
    e.preventDefault()
    if (e.touches && e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.hypot(dx, dy)
      if (lastPinchDist.current) {
        const delta = dist / lastPinchDist.current
        setScale(s => Math.max(0.1, s * delta))
      }
      lastPinchDist.current = dist
      return
    }
    const pt = e.touches ? e.touches[0] : e
    setPos({
      x: posStart.current.x + (pt.clientX - dragStart.current.x),
      y: posStart.current.y + (pt.clientY - dragStart.current.y),
    })
  }, [dragging])

  const handlePointerUp = useCallback(() => {
    setDragging(false)
    lastPinchDist.current = null
  }, [])

  useEffect(() => {
    window.addEventListener('mousemove', handlePointerMove)
    window.addEventListener('mouseup', handlePointerUp)
    window.addEventListener('touchmove', handlePointerMove, { passive: false })
    window.addEventListener('touchend', handlePointerUp)
    return () => {
      window.removeEventListener('mousemove', handlePointerMove)
      window.removeEventListener('mouseup', handlePointerUp)
      window.removeEventListener('touchmove', handlePointerMove)
      window.removeEventListener('touchend', handlePointerUp)
    }
  }, [handlePointerMove, handlePointerUp])

  const handleWheel = (e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.95 : 1.05
    setScale(s => Math.max(0.1, s * delta))
  }

  const handleConfirm = () => {
    if (!imgRef.current) return
    const canvas = canvasRef.current
    const size = 512
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()
    const ratio = size / circleSize
    const sx = pos.x * ratio
    const sy = pos.y * ratio
    const sw = imgRef.current.width * scale * ratio
    const sh = imgRef.current.height * scale * ratio
    ctx.drawImage(imgRef.current, sx, sy, sw, sh)
    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob)
    }, 'image/jpeg', 0.9)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 10000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 20px', paddingTop: '52px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2,
      }}>
        <button onClick={onCancel} style={{
          background: 'none', border: 'none', color: '#fff', fontSize: '17px',
          fontFamily: 'var(--font-system)', cursor: 'pointer', fontWeight: '400',
        }}>Cancel</button>
        <span style={{ color: '#fff', fontSize: '17px', fontWeight: '600' }}>Move and Scale</span>
        <button onClick={handleConfirm} style={{
          background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: '17px',
          fontFamily: 'var(--font-system)', cursor: 'pointer', fontWeight: '600',
        }}>Choose</button>
      </div>
      <div
        ref={containerRef}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onWheel={handleWheel}
        style={{
          width: circleSize + 'px', height: circleSize + 'px',
          borderRadius: '50%', overflow: 'hidden', position: 'relative',
          border: '2px solid rgba(255,255,255,0.3)', cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        {imgLoaded && imgRef.current && (
          <img
            src={imageUrl}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              left: pos.x + 'px',
              top: pos.y + 'px',
              width: (imgRef.current.width * scale) + 'px',
              height: (imgRef.current.height * scale) + 'px',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />
        )}
      </div>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '16px' }}>
        Drag to reposition · {typeof window.ontouchstart !== 'undefined' ? 'Pinch' : 'Scroll'} to zoom
      </p>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  )
}


function SettingsPage({
  session, showProfileSettings, setShowProfileSettings,
  profilePhotoUrl, displayName,
  onProfilePhotoSelect, onDisplayNameChange, onProfileCropConfirm,
  showPrivacyPolicy, setShowPrivacyPolicy, onSignOut,
  savingProfile, onShowSubscription
}) {

  const [cropImage, setCropImage] = useState(null)
  const mainCardFileRef = useRef(null)

  // When user picks a file, show crop modal
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const url = URL.createObjectURL(file)
    setCropImage(url)
  }

  const handleCropConfirm = (blob) => {
    setCropImage(null)
    if (onProfileCropConfirm) onProfileCropConfirm(blob)
  }

  // Shared styles
  const sectionLabel = { fontSize: '15px', color: '#747474', marginBottom: '10px', fontWeight: '700' }
  const cardBoxDark = { background: '#242424', borderRadius: '16px', overflow: 'hidden' }
  const dividerLine = { height: '0.5px', background: '#666666', margin: '0 16px' }
  const rowItem = { padding: '14px 20px', fontSize: '16px', color: '#fff', cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'var(--font-system)' }

  if (showProfileSettings) return (
    <div className="app-content" style={{ height: '100vh', display: 'flex', flexDirection: 'column', paddingTop: '0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0', paddingTop: '48px', position: 'relative' }}>
        <button onClick={() => setShowProfileSettings(false)}
          style={{ position: 'absolute', left: '0', width: '44px', height: '44px', borderRadius: '22px', background: 'var(--color-bg-elevated)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-primary)' }}>
          <svg width="12" height="21" viewBox="0 0 8 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 5.18555C0.00585938 4.98633 0.0761719 4.81641 0.234375 4.66406L4.80469 0.193359C4.92773 0.0644531 5.0918 0 5.2793 0C5.66016 0 5.95898 0.292969 5.95898 0.673828C5.95898 0.861328 5.88281 1.03125 5.75391 1.16602L1.63477 5.18555L5.75391 9.20508C5.88281 9.33398 5.95898 9.50391 5.95898 9.69141C5.95898 10.0781 5.66016 10.3711 5.2793 10.3711C5.0918 10.3711 4.92773 10.3066 4.80469 10.1777L0.234375 5.70703C0.0761719 5.55469 0 5.38477 0 5.18555Z" fill="currentColor"/>
          </svg>
        </button>
        <span style={{ fontSize: '17px', fontWeight: '600', color: '#fff' }}>Profile Settings</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0 24px' }}>
          <label style={{ cursor: 'pointer', position: 'relative' }}>
            {profilePhotoUrl
              ? <img src={profilePhotoUrl} alt="Profile" style={{ width: '120px', height: '120px', borderRadius: '60px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.15)' }} />
              : <div style={{ width: '120px', height: '120px', borderRadius: '60px', background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', color: '#fff', fontWeight: '600', border: '2px solid rgba(255,255,255,0.15)' }}>
                  {(displayName || session.user.email)?.[0]?.toUpperCase() || 'U'}
                </div>
            }
            <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>
          <label style={{ cursor: 'pointer' }}>
            <span style={{ color: 'var(--color-text-link)', fontSize: '15px', fontWeight: '500', marginTop: '12px', display: 'block' }}>Edit Photo</span>
            <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>
          {savingProfile && <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Saving...</p>}
        </div>
        <p style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '8px', fontWeight: '600' }}>Profile Info</p>
        <div style={{ ...cardBoxDark, marginBottom: '16px' }}>
          <input type="text" placeholder="Name" value={displayName} onChange={e => onDisplayNameChange(e.target.value)}
            style={{ width: '100%', padding: '14px 20px', background: 'none', border: 'none', color: '#fff', fontSize: '16px', outline: 'none', fontFamily: 'var(--font-system)' }} />
        </div>
        <p style={{ fontSize: '13px', color: '#c0c0c0', margin: '0', lineHeight: '1.5' }}>
          Your name and photo are synced to your account and will appear on any device you sign in on.
        </p>
      </div>

      {cropImage && (
        <CropModal
          imageUrl={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropImage(null)}
        />
      )}
    </div>
  )

  return (
    <div className="app-content no-sb" style={{ height: '100vh', overflowY: 'auto', paddingTop: '48px', paddingBottom: '100px' }}>
      <h1 style={{ fontSize: '34px', fontWeight: '700', marginBottom: '24px', color: '#fff' }}>Settings</h1>

      {/* Hidden file input for main card profile pic */}
      <input ref={mainCardFileRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

      {/* Profile card */}
      <div onClick={() => setShowProfileSettings(true)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', ...cardBoxDark, marginBottom: '28px', cursor: 'pointer', borderRadius: '20px' }}>
        {/* Profile pic — clicking it opens file picker directly, not profile settings */}
        <div
          style={{ flexShrink: 0, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation()
            mainCardFileRef.current?.click()
          }}
        >
          {profilePhotoUrl
            ? <img src={profilePhotoUrl} alt="Profile" style={{ width: '52px', height: '52px', borderRadius: '26px', objectFit: 'cover' }} />
            : <div style={{ width: '52px', height: '52px', borderRadius: '26px', background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: '#fff', fontWeight: '600' }}>
                {(displayName || session.user.email)?.[0]?.toUpperCase() || 'U'}
              </div>
          }
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '20px', fontWeight: '600', color: '#fff', margin: '0 0 4px 0' }}>{displayName || session.user.email.split('@')[0]}</p>
          <p style={{ fontSize: '14px', color: '#747474', margin: 0 }}>{session.user.email}</p>
        </div>
        <svg width="8" height="16" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.95898 5.18555C5.95898 5.38477 5.88867 5.55469 5.73047 5.70703L1.16016 10.1777C1.03125 10.3066 0.873047 10.3711 0.685547 10.3711C0.304688 10.3711 0 10.0781 0 9.69141C0 9.50391 0.0820312 9.33398 0.205078 9.20508L4.33008 5.18555L0.205078 1.16602C0.0761719 1.03125 0 0.861328 0 0.673828C0 0.292969 0.304688 0 0.685547 0C0.873047 0 1.03125 0.0644531 1.16016 0.193359L5.73047 4.66406C5.88281 4.81641 5.95898 4.98633 5.95898 5.18555Z" fill="#666"/>
        </svg>
      </div>

      {/* Billing */}
      <p style={sectionLabel}>Billing</p>
      <div style={{ ...cardBoxDark, marginBottom: '28px' }}>
        <button
          onClick={() => onShowSubscription?.()}
          style={{ ...rowItem, color: 'var(--color-text-link)' }}
        >
          Free Trial
        </button>
      </div>

      {/* About */}
      <p style={sectionLabel}>About</p>
      <div style={{ ...cardBoxDark, marginBottom: '28px' }}>
        <button style={rowItem}>Feedback</button>
        <div style={dividerLine} />
        <button style={rowItem}>Help Center</button>
        <div style={dividerLine} />
        <button onClick={() => setShowPrivacyPolicy(true)} style={rowItem}>Terms Condition & Privacy Policy</button>
        <div style={dividerLine} />
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '16px', color: '#fff' }}>App Version</span>
          <span style={{ fontSize: '16px', color: '#999' }}>AirLog v1.0.0</span>
        </div>
      </div>

      {/* Log out */}
      <button onClick={onSignOut}
        style={{ width: '100%', padding: '16px 20px', background: '#242424', border: 'none', borderRadius: '100px', color: '#ff3434', fontSize: '16px', textAlign: 'center', cursor: 'pointer', fontWeight: '400', fontFamily: 'var(--font-system)' }}>
        Log out
      </button>

      <PrivacyPolicy show={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />

      {/* Crop modal can also appear from main settings page */}
      {cropImage && (
        <CropModal
          imageUrl={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropImage(null)}
        />
      )}
    </div>
  )
}

export default SettingsPage